import React, { useEffect, useState, useCallback } from "react";
import axios from 'axios';
import { getCurrentComCode, getCurrentUser } from "../api/common";
import { getManager } from "../api/managerApi";
import {
  PORTLET_LAYOUTS,
  managerToLayoutNumber,
  parseManagerSelectedPortlet,
  setPortletItemsFromDb,
} from '../utils/managerUtils';
import { getPortlets } from '../api/managerApi';
import { renderPortletById } from './manager/items/PortletList';
import ViewPost from "./board/ViewPost";
import AprvDocView from "./aprvdoc/AprvDocView";

import "../css/portal.css";

const isTrueValue = (value) => value === true || value === 1 || value === '1' || value === 'true';

const withCacheBuster = (src, version) => {
  if (!src) return '';
  const separator = src.includes('?') ? '&' : '?';
  return `${src}${separator}v=${version}`;
};

const getPortalColumnClassName = cols => {
  const widthMap = {
    1: 'col-12 col-xl-12',
    2: 'col-12 col-xl-6',
    3: 'col-12 col-xl-4',
  };
  return `${widthMap[cols] || 'col-12'} d-flex flex-column`;
};

function Portal() {
  const [currentUser, setCurrentUser] = useState(null);
  const [portalInfo, setPortalInfo] = useState(null);
  const [portalImageVersion, setPortalImageVersion] = useState(Date.now());
  const [ssoReady, setSsoReady] = useState(() => !!window.mailSsoReady);

  // 상세 보기 상태
  const [originalView, setOriginalView] = useState(false);
  const [originalType, setOriginalType] = useState(null); // 'board' | 'approval'
  const [originalId, setOriginalId] = useState(null); // 게시글 id 또는 결재문서 id
  const [originalCodeId, setOriginalCodeId] = useState(null);

  // 상세 보기 트리거 함수
  const handleShowOriginal = (type, id, codeId = null) => {
    setOriginalType(type);
    setOriginalId(id);
    setOriginalCodeId(codeId);
    setOriginalView(true);
  };

  // 상세 보기 닫기 함수
  const handleCloseOriginal = () => {
    setOriginalView(false);
    setOriginalType(null);
    setOriginalId(null);
    setOriginalCodeId(null);
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = sessionStorage.getItem("token");

        const basicUser = await getCurrentUser(token);

        if (basicUser && basicUser.id) {
          const res = await axios.get(`/api/auth/users/${basicUser.id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          //console.log(res.data);
          setCurrentUser(res.data);
        } else {
          setCurrentUser(basicUser);
        }
      } catch (err) {
        console.error("사용자 정보 불러오기 실패:", err);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    getPortlets()
      .then(portletRows => setPortletItemsFromDb(portletRows))
      .catch(err => console.error('포틀릿 목록 초기화 실패:', err));
  }, []);

  const fetchPortalInfo = useCallback(async () => {
    try {
      const M_ComCode = getCurrentComCode();
      if (!M_ComCode) {
        console.error('포탈 설정 정보 불러오기 실패: M_ComCode가 없습니다.');
        return;
      }
      const manager = await getManager(M_ComCode);
      setPortalInfo(manager);
      setPortalImageVersion(Date.now());
    } catch (err) {
      console.error('포탈 설정 정보 불러오기 실패:', err);
    }
  }, []);

  useEffect(() => {
    fetchPortalInfo();
    window.addEventListener('managerDataChanged', fetchPortalInfo);
    return () => window.removeEventListener('managerDataChanged', fetchPortalInfo);
  }, [fetchPortalInfo]);

  useEffect(() => {
    if (window.mailSsoReady) {
      setSsoReady(true);
      return;
    }
    const handler = () => setSsoReady(true);
    window.addEventListener('mail-sso-ready', handler);
    return () => window.removeEventListener('mail-sso-ready', handler);
  }, []);

  const selectedLayout = managerToLayoutNumber(portalInfo?.P_PortletTypeNum);
  const selectedPortlets = parseManagerSelectedPortlet(portalInfo?.P_SelectPortlet, selectedLayout);

  if (originalView && originalType && originalId) {
    return (
      <div className="portal-page container-fluid">
        <div className="row">
          <div className="col-12">
            <button className="btn btn-secondary mb-3" onClick={handleCloseOriginal}>
              ← 포틀릿으로 돌아가기
            </button>
            {originalType === 'board' && <ViewPost id={originalId} codeId={originalCodeId} />}
            {originalType === 'approval' && <AprvDocView aprvId={originalId} />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-page container-fluid">
      {isTrueValue(portalInfo?.P_UseBanner) && portalInfo?.P_BannerPath ? (
        <div className="row mb-3">
          <div className="col-12">
            <img
              src={withCacheBuster(portalInfo.P_BannerPath, portalImageVersion)}
              alt="포탈 배너"
              style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', borderRadius: '10px' }}
            />
          </div>
        </div>
      ) : null}

      {PORTLET_LAYOUTS[selectedLayout].rows.map((cols, rowIdx) => (
        <div className="row" key={`portal-row-${rowIdx}`}>
          {Array.from({ length: cols }).map((_, colIdx) => {
            const zoneKey = `${rowIdx}-${colIdx}`;
            const portlet = selectedPortlets[zoneKey];
            return (
              <div className={getPortalColumnClassName(cols)} key={zoneKey}>
                {renderPortletById(portlet?.id, { currentUser, ssoReady, onShowOriginal: handleShowOriginal })}
              </div>
            );
          })}
        </div>
      ))}

      {isTrueValue(portalInfo?.P_UseCR) && portalInfo?.P_CRText ? (
        <div className="row mt-2">
          <div className="col-12 text-center text-muted" style={{ fontSize: '12px' }}>
            {portalInfo.P_CRText}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Portal;